import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileSkeleton } from '../Common/PageSkeleton';
import api from '../../config/api';
import { clearSessionToken } from '../../utils/authSession';
import './Profile.css';
import {
    Loader2, Camera, User, Mail, Phone, Lock, Save, ArrowLeft, CheckCircle2,
    Heart, Ticket, Wallet as WalletIcon, MapPin, Utensils, HelpCircle,
    MessageSquare, Bell, CreditCard, Trash2, ShieldCheck, FileText, LogOut, ChevronRight,
    RotateCcw, Info, Download
} from 'lucide-react';
import userAPI from '../../services/userAPI';
import PhoneInput from '../Common/PhoneInput';
import {
    DEFAULT_PHONE_COUNTRY,
    buildPhonePayload,
    parseStoredPhone,
    isPhoneComplete,
    getCurrencyFromPhone,
} from '../../utils/phone';
import { formatMoney } from '../../utils/formatMoney';
import NotificationBell from '../Common/NotificationBell';
import FormBanner from '../Common/FormBanner';

const Profile = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [user, setUser] = useState(null);
    const [view, setView] = useState('dashboard'); // 'dashboard' or 'edit'
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: {
            countryCode: DEFAULT_PHONE_COUNTRY,
            nationalNumber: '',
        },
        profilePic: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    // OTP State
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const res = await api.get('/user/profile');
            setUser(res.data);
            setFormData({
                name: res.data.name || '',
                email: res.data.email || '',
                phone: parseStoredPhone(res.data.mobile),
                profilePic: res.data.profilePic || ''
            });
            setPreviewUrl(res.data.profilePic || '');
        } catch (err) {
            console.error('Error fetching profile:', err);
            setMessage({ type: 'error', text: 'Failed to load profile' });
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('email', formData.email);
            if (!isPhoneComplete(formData.phone)) {
                setMessage({ type: 'error', text: 'Please enter a valid mobile number' });
                setSaving(false);
                return;
            }
            const phonePayload = buildPhonePayload(formData.phone);
            data.append('mobile', phonePayload.mobile);
            data.append('countryCode', phonePayload.countryCode);
            if (selectedFile) {
                data.append('profilePic', selectedFile);
            }

            const res = await api.put('/user/profile', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.requiresOTP) {
                setShowOtpModal(true);
                setMessage({ type: 'info', text: res.data.message });
            } else {
                setUser(res.data.user);
                setMessage({ type: 'success', text: 'Profile updated successfully!' });
                setSelectedFile(null);
                setTimeout(() => setView('dashboard'), 1500);
            }
        } catch (err) {
            setMessage({
                type: 'error',
                text: err.response?.data?.message || 'Error updating profile'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp || otp.length !== 6) {
            setOtpError('Please enter a valid 6-digit OTP');
            return;
        }

        setOtpLoading(true);
        setOtpError('');
        try {
            const res = await api.post('/user/profile/verify-otp', { otp });
            setUser(res.data.user);
            setShowOtpModal(false);
            setMessage({ type: 'success', text: 'Profile verified and updated!' });
            setOtp('');
            setSelectedFile(null);
            setFormData({
                name: res.data.user.name || '',
                email: res.data.user.email || '',
                phone: parseStoredPhone(res.data.user.mobile),
                profilePic: res.data.user.profilePic || ''
            });
            setTimeout(() => setView('dashboard'), 1500);
        } catch (err) {
            setOtpError(err.response?.data?.message || 'OTP verification failed');
        } finally {
            setOtpLoading(false);
        }
    };

    const [downloadingData, setDownloadingData] = useState(false);
    const handleDownloadData = async () => {
        if (downloadingData) return;
        setDownloadingData(true);
        try {
            await userAPI.downloadMyData();
        } catch (err) {
            alert(err.response?.status === 429 ? 'Too many requests. Please try again later.' : 'Could not download your data. Please try again.');
        } finally {
            setDownloadingData(false);
        }
    };

    const handleLogout = () => {
        clearSessionToken();
        try {
            localStorage.removeItem('token');
        } catch (e) {
            // ignore storage errors
        }
        localStorage.setItem('waseeny_isAuthenticated', 'false');
        navigate('/home');
    };

    if (loading) {
        return <ProfileSkeleton />;
    }


    if (view === 'edit') {
        return (
            <div className="profile-edit-page">
                <div className="profile-header-bar">
                    <button onClick={() => setView('dashboard')} className="back-btn">
                        <ArrowLeft size={20} />
                    </button>
                    <h1>Edit Profile</h1>
                    <div style={{ width: 40 }}></div>
                </div>

                <div className="profile-container">
                    <form onSubmit={handleSubmit} className="profile-form">
                        <div className="avatar-section">
                            <div className="avatar-wrapper">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Profile" className="profile-img" />
                                ) : (
                                    <div className="profile-placeholder">
                                        <User size={60} />
                                    </div>
                                )}
                                <label htmlFor="avatar-upload" className="upload-badge">
                                    <Camera size={18} />
                                </label>
                                <input
                                    type="file"
                                    id="avatar-upload"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>

                        {message.text && (
                            <div className={`message-alert ${message.type}`}>
                                {message.type === 'success' && <CheckCircle2 size={16} />}
                                <span>{message.text}</span>
                            </div>
                        )}

                        <div className="form-sections">
                            <div className="input-group">
                                <label><User size={16} /> Full Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter your name"
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label><Mail size={16} /> Email Address</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="Enter email"
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label><Phone size={16} /> Mobile Number</label>
                                <PhoneInput
                                    label=""
                                    value={formData.phone}
                                    onChange={(phone) => setFormData({ ...formData, phone })}
                                    required
                                />
                            </div>
                        </div>

                        <button type="submit" className="save-btn" disabled={saving}>
                            {saving ? <Loader2 className="spinner" size={18} /> : <Save size={18} />}
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </form>
                </div>
                {showOtpModal && (
                    <div className="otp-modal-overlay">
                        <div className="otp-modal">
                            <h3>Verify Your Changes</h3>
                            <p>We've sent a 6-digit OTP to your new contact details.</p>
                            <FormBanner
                                message={otpError}
                                type="error"
                                onDismiss={() => setOtpError('')}
                            />
                            <input
                                type="text"
                                maxLength="6"
                                value={otp}
                                onChange={(e) => {
                                    setOtp(e.target.value.replace(/\D/g, ''));
                                    setOtpError('');
                                }}
                                placeholder="000000"
                                className="otp-input-field"
                            />
                            <div className="modal-actions">
                                <button className="verify-btn" onClick={handleVerifyOtp} disabled={otpLoading}>
                                    {otpLoading ? 'Verifying...' : 'Verify OTP'}
                                </button>
                                <button className="cancel-btn" onClick={() => { setShowOtpModal(false); setOtpError(''); }}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="profile-dashboard-page">
            <div className="premium-profile-header-container">
                <button onClick={() => navigate(-1)} className="header-back-btn">
                    <ArrowLeft size={20} />
                </button>
                <div style={{ marginLeft: 'auto', marginRight: '10px' }}>
                    <NotificationBell />
                </div>
                <div className="header-profile-info">
                    <div className="dashboard-avatar-wrapper">
                        {user?.profilePic ? (
                            <img src={user.profilePic} alt="Profile" />
                        ) : (
                            <User size={40} color="#C5192D" />
                        )}
                    </div>
                    <h2 className="dashboard-user-name">{user?.name || 'User'}</h2>
                    <p className="dashboard-user-phone">
                        <span className="country-flag">🇰🇼</span> {user?.mobile?.replace(/^(kw|\+965|\+91)\s*/i, '').trim() || '+965 9584 9306'}
                    </p>
                </div>
            </div>

            <div className="dashboard-content">
                <FormBanner
                    message={message.text}
                    type={message.type || 'error'}
                    onDismiss={() => setMessage({ type: '', text: '' })}
                />
                <div className="dashboard-grid-container">
                    <div className="dashboard-main-column">
                        <div className="dashboard-top-actions">
                            <div className="action-card" onClick={() => navigate('/favorites')}>
                                <div className="icon-circle">
                                    <Heart size={22} />
                                </div>
                                <span>Favourites</span>
                            </div>
                            <div className="action-card" onClick={() => navigate('/coupons')}>
                                <div className="icon-circle">
                                    <Ticket size={22} />
                                </div>
                                <span>Coupons</span>
                            </div>
                        </div>

                        <div className="dashboard-wallet-row" onClick={() => navigate('/wallet')}>
                            <div className="wallet-left">
                                <WalletIcon size={20} className="wallet-icon" />
                                <span>My Wallet Balance</span>
                            </div>
                            <span className="wallet-balance">{formatMoney(user?.walletBalance, { symbol: user?.currencySymbol || getCurrencyFromPhone(user?.mobile) })}</span>
                        </div>
                    </div>
                </div>

                <div className="dashboard-sections">
                    <div className="dashboard-section">
                        <h3 className="section-title">Account</h3>
                        <div className="section-list">
                            <div className="list-item" onClick={() => setView('edit')}>
                                <div className="item-left"><User size={20} /> <span>Your Profile</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/addresses')}>
                                <div className="item-left"><MapPin size={20} /> <span>Delivery Address</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/refunds')}>
                                <div className="item-left"><RotateCcw size={20} /> <span>My Refunds</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/notifications')}>
                                <div className="item-left"><Bell size={20} /> <span>Notifications</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/food-preferences')}>
                                <div className="item-left"><Utensils size={20} /> <span>Food Preference</span></div>
                                <ChevronRight size={18} />
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-section">
                        <h3 className="section-title">Help & Support</h3>
                        <div className="section-list">
                            <div className="list-item" onClick={() => navigate('/faq')}>
                                <div className="item-left"><HelpCircle size={20} /> <span>FAQ's</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/contact-support')}>
                                <div className="item-left"><MessageSquare size={20} /> <span>Contact Support</span></div>
                                <ChevronRight size={18} />
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-section">
                        <h3 className="section-title">Settings</h3>
                        <div className="section-list">
                            <div className="list-item" onClick={() => navigate('/payment-methods')}>
                                <div className="item-left"><CreditCard size={20} /> <span>Payment Setting</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/change-password')}>
                                <div className="item-left"><Lock size={20} /> <span>Change Password</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={handleDownloadData}>
                                <div className="item-left">{downloadingData ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />} <span>Download My Data</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item delete-item" onClick={() => setShowDeleteModal(true)}>
                                <div className="item-left"><Trash2 size={20} /> <span>Delete Account</span></div>
                                <ChevronRight size={18} />
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-section">
                        <h3 className="section-title">About App</h3>
                        <div className="section-list">
                            <div className="list-item" onClick={() => navigate('/about-us')}>
                                <div className="item-left"><Info size={20} /> <span>About Us</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/privacy-policy')}>
                                <div className="item-left"><ShieldCheck size={20} /> <span>Privacy Policy</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item" onClick={() => navigate('/terms')}>
                                <div className="item-left"><FileText size={20} /> <span>Terms & Condition</span></div>
                                <ChevronRight size={18} />
                            </div>
                            <div className="list-item logout-item" onClick={handleLogout}>
                                <div className="item-left"><LogOut size={20} /> <span>Logout</span></div>
                                <ChevronRight size={18} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal-content">
                        <div className="delete-modal-header">
                            <div className="delete-icon-circle">
                                <Trash2 size={24} color="#C5192D" />
                            </div>
                            <h3>Delete Account</h3>
                            <p>Are you sure you want to delete your account?</p>
                        </div>

                        <div className="delete-warning-box">
                            <h4>Things to Check Before Deleting an Account:</h4>
                            <ul>
                                <li><strong>Pending Orders</strong> - Make sure you don't have any active or pending deliveries.</li>
                                <li><strong>Saved Addresses & Payment Methods</strong> - These will be permanently erased.</li>
                                <li><strong>Order History</strong> - You will lose access to past receipts and order details.</li>
                            </ul>
                        </div>

                        <div className="delete-modal-actions">
                            <button className="cancel-delete-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="confirm-delete-btn" onClick={() => {
                                setMessage({
                                    type: 'success',
                                    text: 'Account deletion request submitted. Our team will contact you for verification.'
                                });
                                setShowDeleteModal(false);
                            }}>Delete Account</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
